import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Modal } from "@mui/material";
import HandDivider from "../tools/HandDivider";
import YakuList from "../tools/YakuList";

const generateWall = () => {
    const types = ["m", "p", "s"];
    const wall = [];

    // 數牌
    types.forEach((type) => {
        for (let num = 1; num <= 9; num++) {
            wall.push(...Array(4).fill(`${num}${type}`));
        }
    });

    // 字牌
    for (let num = 1; num <= 7; num++) {
        wall.push(...Array(4).fill(`${num}z`));
    }

    return wall;
};

const sortTiles = (tiles) => {
    const typeOrder = { m: 0, p: 1, s: 2, z: 3 };

    return [...tiles].sort((a, b) => {
        const aType = a.slice(-1);
        const aNum = a.replace(/\D/g, "");
        const aSortNum = aNum === "0" ? 5.5 : parseInt(aNum, 10);

        const bType = b.slice(-1);
        const bNum = b.replace(/\D/g, "");
        const bSortNum = bNum === "0" ? 5.5 : parseInt(bNum, 10);

        if (typeOrder[aType] !== typeOrder[bType]) {
            return typeOrder[aType] - typeOrder[bType];
        }
        return aSortNum - bSortNum;
    });
};

function canExtractTiles(wall, choice) {
    // 計算 wall 中各張牌的數量
    const wallCount = new Map();
    for (const tile of wall) {
        wallCount.set(tile, (wallCount.get(tile) || 0) + 1);
    }

    // 計算 choice.tiles 需要的數量
    const neededCount = new Map();
    for (const tile of choice.tiles) {
        neededCount.set(tile, (neededCount.get(tile) || 0) + 1);
    }

    // 檢查 wall 中是否有足夠的牌
    for (const [tile, count] of neededCount) {
        if ((wallCount.get(tile) || 0) < count) {
            return false; // 牌數不足
        }
    }
    return true; // 牌數足夠
}

const generateValidHand = () => {
    const wall = generateWall();

    const sequenceOptions = [];
    const tripletOptions = [];
    const chowOptions = [];
    const pongOptions = [];
    const exposedKongOptions = [];
    const concealedKongOptions = [];

    // 生成選項池
    const createMeld = (type, size) => {
        // 生成吃的順子
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                chowOptions.push({ type: "吃", tiles: [`${i}${suit}`, `${i + 1}${suit}`, `${i + 2}${suit}`] });
            }
        });

        // 生成碰的明刻
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 9; i++) {
                pongOptions.push({ type: "碰", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });
        ["z"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                pongOptions.push({ type: "碰", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });

        // 生成槓的明槓
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 9; i++) {
                exposedKongOptions.push({ type: "明槓", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });
        ["z"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                exposedKongOptions.push({ type: "明槓", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });

        // 生成槓的暗槓
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 9; i++) {
                concealedKongOptions.push({ type: "暗槓", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });
        ["z"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                concealedKongOptions.push({ type: "暗槓", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });

        // 生成手牌的順子
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                sequenceOptions.push({ type: "手牌順子", tiles: [`${i}${suit}`, `${i + 1}${suit}`, `${i + 2}${suit}`] });
            }
        });

        // 生成手牌的刻子
        ["m", "s", "p"].forEach((suit) => {
            for (let i = 1; i <= 9; i++) {
                tripletOptions.push({ type: "手牌刻子", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });
        ["z"].forEach((suit) => {
            for (let i = 1; i <= 7; i++) {
                tripletOptions.push({ type: "手牌刻子", tiles: [`${i}${suit}`, `${i}${suit}`, `${i}${suit}`] });
            }
        });
    };

    createMeld();

    // 選擇自風和場風
    const roundWind = ["東", "南", "西"][Math.floor(Math.random() * 3)];
    const seatWind = ["東", "南", "西", "北"][Math.floor(Math.random() * 4)];

    // 是否是莊家
    let isDealer = seatWind === "東" ? true : false;

    // 選擇門清或副露
    let isMenzen = Math.random() > 0.5;

    // 選擇自摸或榮和
    let isTsumo = Math.random() > 0.5;

    // 是否立直
    let isRiichi = false;

    // 是否雙立直
    let isDaburuRiichi = false;

    // 是否一發
    let isIppatsu = false;

    // 是否海底
    let isHaitei = false;

    // 是否河底
    let isHoutei = false;

    // 是否嶺上開花
    let isRinshan = false;

    // 是否搶槓
    let isChankan = false;

    let selected = [];

    // 選擇門清的面子組合
    if (isMenzen) {
        while (selected.length < 4) {
            const meldType = Math.random();

            let choice;

            // 80% 機率是順子
            if (meldType < 0.8) {
                choice = sequenceOptions[Math.floor(Math.random() * sequenceOptions.length)];
            }

            // 15% 機率是暗刻
            if (meldType >= 0.8 && meldType < 0.95) {
                choice = tripletOptions[Math.floor(Math.random() * tripletOptions.length)];
            }

            // 5% 機率是暗槓
            if (meldType >= 0.95) {
                choice = concealedKongOptions[Math.floor(Math.random() * concealedKongOptions.length)];
            }

            if (canExtractTiles(wall, choice)) {
                selected.push(choice);
                choice.tiles.forEach((t) => {
                    const index = wall.indexOf(t);
                    if (index !== -1) {
                        wall.splice(index, 1);
                    }
                });
            }
        }

        // 選擇雀頭
        const countMap = new Map();
        wall.forEach((tile) => {
            countMap.set(tile, (countMap.get(tile) || 0) + 1);
        });

        const candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2).map(([tile]) => tile);
        const pairTile = candidates[Math.floor(Math.random() * candidates.length)];

        let indexes = [];
        wall.forEach((tile, index) => {
            if (tile === pairTile) {
                indexes.push(index);
            }
        });

        if (indexes.length >= 2) {
            wall.splice(indexes[1], 1);
            wall.splice(indexes[0], 1);
        }

        selected.push({ type: "手牌雀頭", tiles: [pairTile, pairTile] });

        const riichiType = Math.random();
        isRiichi = riichiType < 0.8; // 80% 立直
        if (roundWind === "東") {
            isDaburuRiichi = riichiType >= 0.8 && riichiType < 0.85; // 5% * 1/3 雙立直
        }

        if (isRiichi || isDaburuRiichi) {
            isIppatsu = Math.random() < 0.05; // 5% 一發
        }

        if (isTsumo) {
            isHaitei = Math.random() < 0.01; // 1% 海底

            if (!isHaitei && !isIppatsu && selected.some((item) => item.type === "暗槓")) {
                isRinshan = Math.random() < 0.01; // 1% 機率嶺上開花
            }
        }

        if (!isTsumo) {
            isHoutei = Math.random() < 0.01; // 1% 河底

            if (!isHoutei) {
                isChankan = Math.random() < 0.01; // 1% 搶槓
            }
        }
    }

    // 選擇副露的面子組合
    if (!isMenzen) {
        // 由於副露無法立直，所以需要有役才會副露，下面主要分為役牌、斷么九和混一色
        const yakuType = Math.random();

        // 40% 機率有役牌
        if (yakuType < 0.4) {
            let choice;

            const yakuHaiOptions = [];

            // 三元牌役牌
            yakuHaiOptions.push({ type: "手牌刻子", tiles: ["5z", "5z", "5z"] });
            yakuHaiOptions.push({ type: "手牌刻子", tiles: ["6z", "6z", "6z"] });
            yakuHaiOptions.push({ type: "手牌刻子", tiles: ["7z", "7z", "7z"] });
            yakuHaiOptions.push({ type: "碰", tiles: ["5z", "5z", "5z"] });
            yakuHaiOptions.push({ type: "碰", tiles: ["6z", "6z", "6z"] });
            yakuHaiOptions.push({ type: "碰", tiles: ["7z", "7z", "7z"] });

            // 場風或自風役牌
            if (roundWind === "東" || seatWind === "東") {
                yakuHaiOptions.push({ type: "手牌刻子", tiles: ["1z", "1z", "1z"] });
                yakuHaiOptions.push({ type: "碰", tiles: ["1z", "1z", "1z"] });
            }
            if (roundWind === "南" || seatWind === "南") {
                yakuHaiOptions.push({ type: "手牌刻子", tiles: ["2z", "2z", "2z"] });
                yakuHaiOptions.push({ type: "碰", tiles: ["2z", "2z", "2z"] });
            }
            if (roundWind === "西" || seatWind === "西") {
                yakuHaiOptions.push({ type: "手牌刻子", tiles: ["3z", "3z", "3z"] });
                yakuHaiOptions.push({ type: "碰", tiles: ["3z", "3z", "3z"] });
            }
            if (seatWind === "北") {
                yakuHaiOptions.push({ type: "手牌刻子", tiles: ["4z", "4z", "4z"] });
                yakuHaiOptions.push({ type: "碰", tiles: ["4z", "4z", "4z"] });
            }

            // 先選一組面子是役牌
            choice = yakuHaiOptions[Math.floor(Math.random() * yakuHaiOptions.length)];

            if (canExtractTiles(wall, choice)) {
                selected.push(choice);
                choice.tiles.forEach((t) => {
                    const index = wall.indexOf(t);
                    if (index !== -1) {
                        wall.splice(index, 1);
                    }
                });
            }

            // 剩下的面子可以任意選擇
            while (selected.length < 4) {
                const meldType = Math.random();

                // 30% 機率是順子
                if (meldType < 0.3) {
                    choice = sequenceOptions[Math.floor(Math.random() * sequenceOptions.length)];
                }

                // 30% 機率是吃
                if (meldType >= 0.3 && meldType < 0.6) {
                    choice = chowOptions[Math.floor(Math.random() * chowOptions.length)];
                }

                // 15% 機率是暗刻
                if (meldType >= 0.6 && meldType < 0.75) {
                    choice = tripletOptions[Math.floor(Math.random() * tripletOptions.length)];
                }

                // 15% 機率是碰
                if (meldType >= 0.75 && meldType < 0.9) {
                    choice = pongOptions[Math.floor(Math.random() * pongOptions.length)];
                }

                // 5% 機率是暗槓
                if (meldType >= 0.9 && meldType < 0.95) {
                    choice = concealedKongOptions[Math.floor(Math.random() * concealedKongOptions.length)];
                }

                // 5% 機率是明槓
                if (meldType >= 0.95) {
                    choice = exposedKongOptions[Math.floor(Math.random() * exposedKongOptions.length)];
                }

                if (canExtractTiles(wall, choice)) {
                    selected.push(choice);
                    choice.tiles.forEach((t) => {
                        const index = wall.indexOf(t);
                        if (index !== -1) {
                            wall.splice(index, 1);
                        }
                    });
                }
            }

            // 選擇雀頭
            const countMap = new Map();
            wall.forEach((tile) => {
                countMap.set(tile, (countMap.get(tile) || 0) + 1);
            });

            const candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2).map(([tile]) => tile);
            const pairTile = candidates[Math.floor(Math.random() * candidates.length)];

            let indexes = [];
            wall.forEach((tile, index) => {
                if (tile === pairTile) {
                    indexes.push(index);
                }
            });

            if (indexes.length >= 2) {
                wall.splice(indexes[1], 1);
                wall.splice(indexes[0], 1);
            }

            selected.push({ type: "手牌雀頭", tiles: [pairTile, pairTile] });
        }

        // 30% 機率斷么九
        if (yakuType >= 0.4 && yakuType < 0.7) {
            let choice;

            // 篩選出符合斷么九的面子
            const filteredSequenceOptions = sequenceOptions.filter((_, index) => (index >= 1 && index <= 5) || (index >= 8 && index <= 12) || (index >= 15 && index <= 19));
            const filteredChowOptions = chowOptions.filter((_, index) => (index >= 1 && index <= 5) || (index >= 8 && index <= 12) || (index >= 15 && index <= 19));
            const filteredTripletOptions = tripletOptions.filter((_, index) => (index >= 1 && index <= 7) || (index >= 10 && index <= 16) || (index >= 19 && index <= 25));
            const filteredPongOptions = pongOptions.filter((_, index) => (index >= 1 && index <= 7) || (index >= 10 && index <= 16) || (index >= 19 && index <= 25));
            const filteredConcealedKongOptions = concealedKongOptions.filter((_, index) => (index >= 1 && index <= 7) || (index >= 10 && index <= 16) || (index >= 19 && index <= 25));
            const filteredExposedKongOptions = exposedKongOptions.filter((_, index) => (index >= 1 && index <= 7) || (index >= 10 && index <= 16) || (index >= 19 && index <= 25));

            while (selected.length < 4) {
                const meldType = Math.random();

                // 30% 機率是順子
                if (meldType < 0.3) {
                    choice = filteredSequenceOptions[Math.floor(Math.random() * filteredSequenceOptions.length)];
                }

                // 30% 機率是吃
                if (meldType >= 0.3 && meldType < 0.6) {
                    choice = filteredChowOptions[Math.floor(Math.random() * filteredChowOptions.length)];
                }

                // 15% 機率是暗刻
                if (meldType >= 0.6 && meldType < 0.75) {
                    choice = filteredTripletOptions[Math.floor(Math.random() * filteredTripletOptions.length)];
                }

                // 15% 機率是碰
                if (meldType >= 0.75 && meldType < 0.9) {
                    choice = filteredPongOptions[Math.floor(Math.random() * filteredPongOptions.length)];
                }

                // 5% 機率是暗槓
                if (meldType >= 0.9 && meldType < 0.95) {
                    choice = filteredConcealedKongOptions[Math.floor(Math.random() * filteredConcealedKongOptions.length)];
                }

                // 5% 機率是明槓
                if (meldType >= 0.95) {
                    choice = filteredExposedKongOptions[Math.floor(Math.random() * filteredExposedKongOptions.length)];
                }

                if (canExtractTiles(wall, choice)) {
                    selected.push(choice);
                    choice.tiles.forEach((t) => {
                        const index = wall.indexOf(t);
                        if (index !== -1) {
                            wall.splice(index, 1);
                        }
                    });
                }
            }

            // 選擇非么九的雀頭
            const countMap = new Map();
            wall.forEach((tile) => {
                countMap.set(tile, (countMap.get(tile) || 0) + 1);
            });

            const excludedTiles = new Set(["1m", "9m", "1p", "9p", "1s", "9s", "1z", "2z", "3z", "4z", "5z", "6z", "7z"]);

            const candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2 && !excludedTiles.has(tile)).map(([tile]) => tile);
            const pairTile = candidates[Math.floor(Math.random() * candidates.length)];

            let indexes = [];
            wall.forEach((tile, index) => {
                if (tile === pairTile) {
                    indexes.push(index);
                }
            });

            if (indexes.length >= 2) {
                wall.splice(indexes[1], 1);
                wall.splice(indexes[0], 1);
            }

            selected.push({ type: "手牌雀頭", tiles: [pairTile, pairTile] });
        }

        // 20% 機率混一色
        if (yakuType >= 0.7 && yakuType < 0.9) {
            const tileType = Math.random();

            let choice;

            let filteredSequenceOptions;
            let filteredChowOptions;
            let filteredTripletOptions;
            let filteredPongOptions;
            let filteredConcealedKongOptions;
            let filteredExposedKongOptions;

            // 萬子混一色
            if (tileType < 0.33) {
                filteredSequenceOptions = sequenceOptions.filter((_, index) => index >= 0 && index <= 6);
                filteredChowOptions = chowOptions.filter((_, index) => index >= 0 && index <= 6);
                filteredTripletOptions = tripletOptions.filter((_, index) => (index >= 0 && index <= 8) || (index >= 27 && index <= 33));
                filteredPongOptions = pongOptions.filter((_, index) => (index >= 0 && index <= 8) || (index >= 27 && index <= 33));
                filteredConcealedKongOptions = concealedKongOptions.filter((_, index) => (index >= 0 && index <= 8) || (index >= 27 && index <= 33));
                filteredExposedKongOptions = exposedKongOptions.filter((_, index) => (index >= 0 && index <= 8) || (index >= 27 && index <= 33));
            }

            // 索子混一色
            if (tileType >= 0.33 && tileType < 0.67) {
                filteredSequenceOptions = sequenceOptions.filter((_, index) => index >= 7 && index <= 13);
                filteredChowOptions = chowOptions.filter((_, index) => index >= 7 && index <= 13);
                filteredTripletOptions = tripletOptions.filter((_, index) => (index >= 9 && index <= 17) || (index >= 27 && index <= 33));
                filteredPongOptions = pongOptions.filter((_, index) => (index >= 9 && index <= 17) || (index >= 27 && index <= 33));
                filteredConcealedKongOptions = concealedKongOptions.filter((_, index) => (index >= 9 && index <= 17) || (index >= 27 && index <= 33));
                filteredExposedKongOptions = exposedKongOptions.filter((_, index) => (index >= 9 && index <= 17) || (index >= 27 && index <= 33));
            }

            // 餅子混一色
            if (tileType >= 0.67) {
                filteredSequenceOptions = sequenceOptions.filter((_, index) => index >= 14 && index <= 20);
                filteredChowOptions = chowOptions.filter((_, index) => index >= 14 && index <= 20);
                filteredTripletOptions = tripletOptions.filter((_, index) => index >= 18 && index <= 33);
                filteredPongOptions = pongOptions.filter((_, index) => index >= 18 && index <= 33);
                filteredConcealedKongOptions = concealedKongOptions.filter((_, index) => index >= 18 && index <= 33);
                filteredExposedKongOptions = exposedKongOptions.filter((_, index) => index >= 18 && index <= 33);
            }

            while (selected.length < 4) {
                const meldType = Math.random();

                // 30% 機率是順子
                if (meldType < 0.3) {
                    choice = filteredSequenceOptions[Math.floor(Math.random() * filteredSequenceOptions.length)];
                }

                // 30% 機率是吃
                if (meldType >= 0.3 && meldType < 0.6) {
                    choice = filteredChowOptions[Math.floor(Math.random() * filteredChowOptions.length)];
                }

                // 15% 機率是暗刻
                if (meldType >= 0.6 && meldType < 0.75) {
                    choice = filteredTripletOptions[Math.floor(Math.random() * filteredTripletOptions.length)];
                }

                // 15% 機率是碰
                if (meldType >= 0.75 && meldType < 0.9) {
                    choice = filteredPongOptions[Math.floor(Math.random() * filteredPongOptions.length)];
                }

                // 5% 機率是暗槓
                if (meldType >= 0.9 && meldType < 0.95) {
                    choice = filteredConcealedKongOptions[Math.floor(Math.random() * filteredConcealedKongOptions.length)];
                }

                // 5% 機率是明槓
                if (meldType >= 0.95) {
                    choice = filteredExposedKongOptions[Math.floor(Math.random() * filteredExposedKongOptions.length)];
                }

                if (canExtractTiles(wall, choice)) {
                    selected.push(choice);
                    choice.tiles.forEach((t) => {
                        const index = wall.indexOf(t);
                        if (index !== -1) {
                            wall.splice(index, 1);
                        }
                    });
                }
            }

            // 選擇雀頭
            const countMap = new Map();
            wall.forEach((tile) => {
                countMap.set(tile, (countMap.get(tile) || 0) + 1);
            });

            const mTiles = new Set(["1m", "2m", "3m", "4m", "5m", "6m", "7m", "8m", "9m", "1z", "2z", "3z", "4z", "5z", "6z", "7z"]);
            const sTiles = new Set(["1s", "2s", "3s", "4s", "5s", "6s", "7s", "8s", "9s", "1z", "2z", "3z", "4z", "5z", "6z", "7z"]);
            const pTiles = new Set(["1p", "2p", "3p", "4p", "5p", "6p", "7p", "8p", "9p", "1z", "2z", "3z", "4z", "5z", "6z", "7z"]);

            let candidates;

            if (tileType < 0.33) {
                candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2 && mTiles.has(tile)).map(([tile]) => tile);
            }
            if (tileType >= 0.33 && tileType < 0.67) {
                candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2 && sTiles.has(tile)).map(([tile]) => tile);
            }
            if (tileType >= 0.67) {
                candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2 && pTiles.has(tile)).map(([tile]) => tile);
            }

            const pairTile = candidates[Math.floor(Math.random() * candidates.length)];

            let indexes = [];
            wall.forEach((tile, index) => {
                if (tile === pairTile) {
                    indexes.push(index);
                }
            });

            if (indexes.length >= 2) {
                wall.splice(indexes[1], 1);
                wall.splice(indexes[0], 1);
            }

            selected.push({ type: "手牌雀頭", tiles: [pairTile, pairTile] });
        }

        // 10% 其他
        if (yakuType >= 0.9) {
            while (selected.length < 4) {
                const meldType = Math.random();

                let choice;

                // 30% 機率是順子
                if (meldType < 0.3) {
                    choice = sequenceOptions[Math.floor(Math.random() * sequenceOptions.length)];
                }

                // 30% 機率是吃
                if (meldType >= 0.3 && meldType < 0.6) {
                    choice = chowOptions[Math.floor(Math.random() * chowOptions.length)];
                }

                // 15% 機率是暗刻
                if (meldType >= 0.6 && meldType < 0.75) {
                    choice = tripletOptions[Math.floor(Math.random() * tripletOptions.length)];
                }

                // 15% 機率是碰
                if (meldType >= 0.75 && meldType < 0.9) {
                    choice = pongOptions[Math.floor(Math.random() * pongOptions.length)];
                }

                // 5% 機率是暗槓
                if (meldType >= 0.9 && meldType < 0.95) {
                    choice = concealedKongOptions[Math.floor(Math.random() * concealedKongOptions.length)];
                }

                // 5% 機率是明槓
                if (meldType >= 0.95) {
                    choice = exposedKongOptions[Math.floor(Math.random() * exposedKongOptions.length)];
                }

                if (canExtractTiles(wall, choice)) {
                    selected.push(choice);
                    choice.tiles.forEach((t) => {
                        const index = wall.indexOf(t);
                        if (index !== -1) {
                            wall.splice(index, 1);
                        }
                    });
                }
            }

            // 選擇雀頭
            const countMap = new Map();
            wall.forEach((tile) => {
                countMap.set(tile, (countMap.get(tile) || 0) + 1);
            });

            const candidates = [...countMap.entries()].filter(([tile, count]) => count >= 2).map(([tile]) => tile);
            const pairTile = candidates[Math.floor(Math.random() * candidates.length)];

            let indexes = [];
            wall.forEach((tile, index) => {
                if (tile === pairTile) {
                    indexes.push(index);
                }
            });

            if (indexes.length >= 2) {
                wall.splice(indexes[1], 1);
                wall.splice(indexes[0], 1);
            }

            selected.push({ type: "手牌雀頭", tiles: [pairTile, pairTile] });
        }

        if (isTsumo) {
            isHaitei = Math.random() < 0.01; // 1% 海底

            if (!isHaitei && selected.some((item) => item.type.includes("暗槓") || item.type.includes("明槓"))) {
                isRinshan = Math.random() < 0.01; // 1% 機率嶺上開花
            }
        }

        if (!isTsumo) {
            isHoutei = Math.random() < 0.01; // 1% 河底

            if (!isHoutei) {
                isChankan = Math.random() < 0.01; // 1% 搶槓
            }
        }
    }

    // 選擇完面子後如果只包含手牌順子、手牌刻子、雀頭和暗槓，則設定為門清
    if (selected.length === 5 && selected.every((item) => ["手牌順子", "手牌刻子", "手牌雀頭", "暗槓"].includes(item.type))) {
        isMenzen = true;
    }

    // 排序面子
    const typeOrder = {
        手牌順子: 1,
        手牌刻子: 2,
        手牌雀頭: 3,
        吃: 4,
        碰: 5,
        明槓: 6,
        暗槓: 7,
    };
    selected.sort((a, b) => (typeOrder[a.type] ?? Infinity) - (typeOrder[b.type] ?? Infinity));

    // 判斷手牌是否還有其他組成牌的方式
    const handSets = selected.filter((item) => ["手牌順子", "手牌刻子", "手牌雀頭"].includes(item.type)); // 手牌
    const notHandSets = selected.filter((item) => ["吃", "碰", "明槓", "暗槓"].includes(item.type)); // 副露區

    const allHandSetsTiles = [];

    handSets.forEach((set) => {
        allHandSetsTiles.push(...set.tiles);
    });

    const handDivider = new HandDivider();

    const dividedresults = handDivider.decomposeHand(allHandSetsTiles);

    const selectedList = [];

    dividedresults.forEach((result) => {
        selectedList.push([...result, ...notHandSets].sort((a, b) => (typeOrder[a.type] ?? Infinity) - (typeOrder[b.type] ?? Infinity)));
    });

    // 選擇自摸or放銃牌
    let results = []; // 輸出結果陣列

    const uniqueAllHandSetsTiles = [...new Set(allHandSetsTiles)];
    const winningTile = uniqueAllHandSetsTiles[Math.floor(Math.random() * uniqueAllHandSetsTiles.length)];

    selectedList.forEach((selected) => {
        const winningTileData = selected
            .filter((item) => ["手牌順子", "手牌刻子", "手牌雀頭"].includes(item.type))
            .reduce((acc, item, index) => {
                let matchingIndexes = item.tiles.map((tile, tileIndex) => (tile === winningTile ? tileIndex : -1)).filter((tileIndex) => tileIndex !== -1);

                if (matchingIndexes.length > 0) {
                    if (item.type === "手牌刻子") {
                        matchingIndexes = [2]; // 刻子固定紀錄索引 2
                    } else if (item.type === "手牌雀頭") {
                        matchingIndexes = [1]; // 雀頭固定紀錄索引 1
                    }

                    acc.push({ winningMeldIndex: index, winningTileIndex: matchingIndexes[0] });
                }

                return acc;
            }, []);

        winningTileData.forEach((data) => {
            let waitType = "";

            if (selected[data.winningMeldIndex].type === "手牌刻子") {
                waitType = "雙碰";
            } else if (selected[data.winningMeldIndex].type === "手牌雀頭") {
                waitType = "單騎";
            } else if (selected[data.winningMeldIndex].type === "手牌順子") {
                const nums = selected[data.winningMeldIndex].tiles.map((t) => parseInt(t));
                if (data.winningTileIndex === 1) {
                    waitType = "嵌張";
                } else if (data.winningTileIndex === 0) {
                    if (JSON.stringify(nums) === JSON.stringify([7, 8, 9])) {
                        waitType = "邊張";
                    } else {
                        waitType = "兩面";
                    }
                } else if (data.winningTileIndex === 2) {
                    if (JSON.stringify(nums) === JSON.stringify([1, 2, 3])) {
                        waitType = "邊張";
                    } else {
                        waitType = "兩面";
                    }
                }
            }

            // 設定 results 資料
            results.push({
                melds: selected,
                winningMeldIndex: data.winningMeldIndex,
                winningTileIndex: data.winningTileIndex,
                waitType: waitType,
                roundWind: roundWind,
                seatWind: seatWind,
                isDealer: isDealer,
                isMenzen: isMenzen,
                isTsumo: isTsumo,
                isRiichi: isRiichi,
                isDaburuRiichi: isDaburuRiichi,
                isIppatsu: isIppatsu,
                isHaitei: isHaitei,
                isHoutei: isHoutei,
                isRinshan: isRinshan,
                isChankan: isChankan,
            });
        });
    });

    // 判斷手牌役種
    const yakuList = new YakuList();

    results.forEach((result) => {
        result.isTanyao = yakuList.checkTanyao(result.melds);
        result.isRoundEast = yakuList.checkRoundEast(result.melds, result.roundWind);
        result.isRoundSouth = yakuList.checkRoundSouth(result.melds, result.roundWind);
        result.isRoundWest = yakuList.checkRoundWest(result.melds, result.roundWind);
        result.isSeatEast = yakuList.checkSeatEast(result.melds, result.seatWind);
        result.isSeatSouth = yakuList.checkSeatSouth(result.melds, result.seatWind);
        result.isSeatWest = yakuList.checkSeatWest(result.melds, result.seatWind);
        result.isSeatNorth = yakuList.checkSeatNorth(result.melds, result.seatWind);
        result.isHaku = yakuList.checkHaku(result.melds);
        result.isHatsu = yakuList.checkHatsu(result.melds);
        result.isChun = yakuList.checkChun(result.melds);
        result.isMenzenTsumo = yakuList.checkMenzenTsumo(result.isMenzen, result.isTsumo);
        result.isPinfu = yakuList.checkPinfu(result.melds, result.waitType, result.roundWind, result.seatWind);
        result.isIipeikou = yakuList.checkIipeikou(result.melds, result.isMenzen);
        result.isToitoi = yakuList.checkToitoi(result.melds);
        result.isSanankou = yakuList.checkSanankou(result.melds, result.waitType, result.isTsumo);
        result.isSankantsu = yakuList.checkSankantsu(result.melds);
        result.isSanshokuDoko = yakuList.checkSanshokuDoko(result.melds);
        result.isHonrouto = yakuList.checkHonrouto(result.melds);
        result.isShouSangen = yakuList.checkShouSangen(result.melds);
        result.isSanshokuDoujun = yakuList.checkSanshokuDoujun(result.melds);
        result.isIttsu = yakuList.checkIttsu(result.melds);
        result.isChanta = yakuList.checkChanta(result.melds);
        result.isHonitsu = yakuList.checkHonitsu(result.melds);
        result.isJunchan = yakuList.checkJunchan(result.melds);

        // 兩杯口覆蓋一杯口
        if (yakuList.checkRyanpeikou(result.melds) === true) {
            result.isIipeikou = false;
            result.isRyanpeikou = true;
        } else {
            result.isRyanpeikou = false;
        }

        result.isChinitsu = yakuList.checkChinitsu(result.melds);
        result.isDaisangen = yakuList.checkDaisangen(result.melds);
        result.isShousuushii = yakuList.checkShousuushii(result.melds);
        result.isDaisushi = yakuList.checkDaisushi(result.melds);
        result.isTsuuiisou = yakuList.checkTsuuiisou(result.melds);
        result.isRyuuiisou = yakuList.checkRyuuiisou(result.melds);
        result.isChinroutou = yakuList.checkChinroutou(result.melds);
        result.isSuuankou = yakuList.checkSuuankou(result.melds, result.isMenzen, result.isTsumo, result.waitType);
        result.isSuuankouTanki = yakuList.checkSuuankouTanki(result.melds, result.isMenzen, result.waitType);
        result.isChuurenPoutou = yakuList.checkChuurenPoutou(result.melds, result.isMenzen, result.winningMeldIndex, result.winningTileIndex);
        result.isJunseiChuurenPoutou = yakuList.checkJunseiChuurenPoutou(result.melds, result.isMenzen, result.winningMeldIndex, result.winningTileIndex);
        result.isSuukantsu = yakuList.checkSuukantsu(result.melds);
    });

    // 計算寶牌
    const doraIndicatorNum = selected.filter((item) => item.type === "明槓" || item.type === "暗槓").length + 1; // 寶牌數量

    const doraIndicators = [];
    const riichiDoraIndicators = [];

    // 隨機選擇 doraNum 張牌作為寶牌指示牌
    for (let i = 0; i < doraIndicatorNum; i++) {
        doraIndicators.push(wall.splice(Math.floor(Math.random() * wall.length), 1)[0]);
    }

    if (isRiichi || isDaburuRiichi) {
        for (let i = 0; i < doraIndicatorNum; i++) {
            riichiDoraIndicators.push(wall.splice(Math.floor(Math.random() * wall.length), 1)[0]);
        }
    }

    const doras = doraIndicators.map((tile) => {
        const num = parseInt(tile[0], 10); // 取得數字部分
        const type = tile[1]; // 取得牌種 (m, p, s, z)

        if (type === "z") {
            // 字牌的對應關係
            const windDragonMap = {
                "1z": "2z",
                "2z": "3z",
                "3z": "4z",
                "4z": "1z",
                "5z": "6z",
                "6z": "7z",
                "7z": "5z",
            };
            return windDragonMap[tile] || tile; // 回傳對應的寶牌
        } else {
            // 數牌 (萬、筒、索)
            return (num === 9 ? "1" : (num + 1).toString()) + type;
        }
    });

    const riichiDoras = riichiDoraIndicators.map((tile) => {
        const num = parseInt(tile[0], 10); // 取得數字部分
        const type = tile[1]; // 取得牌種 (m, p, s, z)

        if (type === "z") {
            // 字牌的對應關係
            const windDragonMap = {
                "1z": "2z",
                "2z": "3z",
                "3z": "4z",
                "4z": "1z",
                "5z": "6z",
                "6z": "7z",
                "7z": "5z",
            };
            return windDragonMap[tile] || tile; // 回傳對應的寶牌
        } else {
            // 數牌 (萬、筒、索)
            return (num === 9 ? "1" : (num + 1).toString()) + type;
        }
    });

    const allHandTiles = [];
    selected.forEach((item) => {
        allHandTiles.push(...item.tiles);
    });

    const doraNum = allHandTiles.reduce((count, tile) => {
        return count + doras.filter((dora) => dora === tile).length;
    }, 0);

    const riichiDoraNum = allHandTiles.reduce((count, tile) => {
        return count + riichiDoras.filter((dora) => dora === tile).length;
    }, 0);

    // 處理搶槓特殊情況，如果和的牌存在於手牌或寶牌指示牌中，則不可出現搶槓
    if ([...allHandTiles, ...doraIndicators, ...riichiDoraIndicators].filter((tile) => tile === winningTile).length !== 1) {
        results.forEach((result) => {
            result.isChankan = false;
        });
    }

    // 處理赤寶牌，機率替換當中的5m5p5s為0m0p0s
    const counts = { "5m": 0, "5p": 0, "5s": 0 };
    const maxReplacements = { "5m": 0, "5p": 0, "5s": 0 }; // 記錄最多替換幾張
    const akaDoraMap = { "5m": "0m", "5p": "0p", "5s": "0s" };

    allHandTiles.forEach((tile) => {
        if (counts.hasOwnProperty(tile)) counts[tile]++;
    });

    // 根據機率決定每個 tile 最多替換多少張
    Object.keys(counts).forEach((tile) => {
        const num = counts[tile];

        if (num === 4) {
            maxReplacements[tile] = 1; // 100% 替換 1 張
        } else if (num > 0) {
            const probability = num * 0.25; // 1張=25%，2張=50%，3張=75%
            maxReplacements[tile] = Math.random() < probability ? 1 : 0;
        }
    });

    // 記錄每種 tile 已經被替換了幾張

    const akaDoraNum = Object.values(maxReplacements).reduce((sum, val) => sum + val, 0);

    results.forEach((result) => {
        const replacedCount = { "5m": 0, "5p": 0, "5s": 0 };

        result.melds = result.melds.map((group) => {
            const newTiles = [...group.tiles]; // 複製陣列，避免修改原始數據
            for (let i = 0; i < newTiles.length; i++) {
                const tile = newTiles[i];

                // 確保不超過允許的替換數
                if (akaDoraMap[tile] && replacedCount[tile] < maxReplacements[tile]) {
                    newTiles[i] = akaDoraMap[tile]; // 替換成赤寶牌
                    replacedCount[tile]++; // 增加該 tile 的替換數
                    break; // 只替換一張，立刻跳出
                }
            }
            return { ...group, tiles: newTiles };
        });

        result.doraIndicators = doraIndicators;
        result.riichiDoraIndicators = riichiDoraIndicators;
        result.doraNum = doraNum;
        result.riichiDoraNum = riichiDoraNum;
        result.akaDoraNum = akaDoraNum;
    });

    // 算符
    results.forEach((result) => {
        const fuResult = calculateFu(result);
        result.fuResult = fuResult;
    });

    // 算役種
    results.forEach((result) => {
        // 先判斷是否存在役滿
        let yakuman = 0;
        let yakumanList = [];

        if (result.isDaisangen) {
            yakuman = yakuman + 1;
            yakumanList.push("大三元 役滿");
        }
        if (result.isShousuushii) {
            yakuman = yakuman + 1;
            yakumanList.push("小四喜 役滿");
        }
        if (result.isDaisushi) {
            yakuman = yakuman + 2;
            yakumanList.push("大四喜 兩倍役滿");
        }
        if (result.isTsuuiisou) {
            yakuman = yakuman + 1;
            yakumanList.push("字一色 役滿");
        }
        if (result.isRyuuiisou) {
            yakuman = yakuman + 1;
            yakumanList.push("綠一色 役滿");
        }
        if (result.isChinroutou) {
            yakuman = yakuman + 1;
            yakumanList.push("清老頭 役滿");
        }
        if (result.isSuuankou) {
            yakuman = yakuman + 1;
            yakumanList.push("四暗刻 役滿");
        }
        if (result.isSuuankouTanki) {
            yakuman = yakuman + 2;
            yakumanList.push("四暗刻單騎 兩倍役滿");
        }
        if (result.isChuurenPoutou) {
            yakuman = yakuman + 1;
            yakumanList.push("九蓮寶燈 役滿");
        }
        if (result.isJunseiChuurenPoutou) {
            yakuman = yakuman + 2;
            yakumanList.push("純正九蓮寶燈 兩倍役滿");
        }
        if (result.isSuukantsu) {
            yakuman = yakuman + 1;
            yakumanList.push("四槓子 役滿");
        }

        // 沒有役滿再判斷役種
        let han = 0;
        let yakuList = [];

        if (yakuman === 0) {
            if (result.isRiichi) {
                han = han + 1;
                yakuList.push("立直 1 飜");
            }
            if (result.isDaburuRiichi) {
                han = han + 2;
                yakuList.push("雙立直 2 飜");
            }
            if (result.isIppatsu) {
                han = han + 1;
                yakuList.push("一發 1 飜");
            }
            if (result.isHaitei) {
                han = han + 1;
                yakuList.push("海底撈月 1 飜");
            }
            if (result.isHoutei) {
                han = han + 1;
                yakuList.push("河底撈魚 1 飜");
            }
            if (result.isRinshan) {
                han = han + 1;
                yakuList.push("嶺上開花 1 飜");
            }
            if (result.isChankan) {
                han = han + 1;
                yakuList.push("搶槓 1 飜");
            }
            if (result.isTanyao) {
                han = han + 1;
                yakuList.push("斷么九 1 飜");
            }
            if (result.isRoundEast) {
                han = han + 1;
                yakuList.push("場風東 1 飜");
            }
            if (result.isRoundSouth) {
                han = han + 1;
                yakuList.push("場風南 1 飜");
            }
            if (result.isRoundWest) {
                han = han + 1;
                yakuList.push("場風西 1 飜");
            }
            if (result.isSeatEast) {
                han = han + 1;
                yakuList.push("自風東 1 飜");
            }
            if (result.isSeatSouth) {
                han = han + 1;
                yakuList.push("自風南 1 飜");
            }
            if (result.isSeatWest) {
                han = han + 1;
                yakuList.push("自風西 1 飜");
            }
            if (result.isSeatNorth) {
                han = han + 1;
                yakuList.push("自風北 1 飜");
            }
            if (result.isHaku) {
                han = han + 1;
                yakuList.push("三元牌白 1 飜");
            }
            if (result.isHatsu) {
                han = han + 1;
                yakuList.push("三元牌發 1 飜");
            }
            if (result.isChun) {
                han = han + 1;
                yakuList.push("三元牌中 1 飜");
            }
            if (result.isMenzenTsumo) {
                han = han + 1;
                yakuList.push("門清自摸和 1 飜");
            }
            if (result.isPinfu) {
                han = han + 1;
                yakuList.push("平和 1 飜");
            }
            if (result.isIipeikou) {
                han = han + 1;
                yakuList.push("一杯口 1 飜");
            }
            if (result.isToitoi) {
                han = han + 2;
                yakuList.push("對對和 2 飜");
            }
            if (result.isSanankou) {
                han = han + 2;
                yakuList.push("三暗刻 2 飜");
            }
            if (result.isSankantsu) {
                han = han + 2;
                yakuList.push("三槓子 2 飜");
            }
            if (result.isSanshokuDoko) {
                han = han + 2;
                yakuList.push("三色同刻 2 飜");
            }
            if (result.isHonrouto) {
                han = han + 2;
                yakuList.push("混老頭 2 飜");
            }
            if (result.isShouSangen) {
                han = han + 2;
                yakuList.push("小三元 2 飜");
            }
            if (result.isSanshokuDoujun) {
                if (result.isMenzen) {
                    han = han + 2;
                    yakuList.push("三色同順 2 飜");
                } else {
                    han = han + 1;
                    yakuList.push("三色同順副露 1 飜");
                }
            }
            if (result.isIttsu) {
                if (result.isMenzen) {
                    han = han + 2;
                    yakuList.push("一氣通貫 2 飜");
                } else {
                    han = han + 1;
                    yakuList.push("一氣通貫副露 1 飜");
                }
            }
            if (result.isChanta) {
                if (result.isMenzen) {
                    han = han + 2;
                    yakuList.push("混全帶么九 2 飜");
                } else {
                    han = han + 1;
                    yakuList.push("混全帶么九副露 1 飜");
                }
            }
            if (result.isHonitsu) {
                if (result.isMenzen) {
                    han = han + 3;
                    yakuList.push("混一色 3 飜");
                } else {
                    han = han + 2;
                    yakuList.push("混一色副露 2 飜");
                }
            }
            if (result.isJunchan) {
                if (result.isMenzen) {
                    han = han + 3;
                    yakuList.push("純全帶么九 3 飜");
                } else {
                    han = han + 2;
                    yakuList.push("純全帶么九副露 2 飜");
                }
            }
            if (result.isRyanpeikou) {
                han = han + 3;
                yakuList.push("兩杯口 3 飜");
            }
            if (result.isChinitsu) {
                if (result.isMenzen) {
                    han = han + 6;
                    yakuList.push("清一色 6 飜");
                } else {
                    han = han + 5;
                    yakuList.push("清一色副露 5 飜");
                }
            }
        }

        let isYaku = false;
        // 統計完全部役種，記錄無役的情況
        if (yakuman === 0 && han === 0) {
            isYaku = false;
        } else {
            isYaku = true;
        }

        // 繼續統計寶牌的飜
        if (yakuman === 0) {
            if (result.doraNum !== 0) {
                han = han + result.doraNum;
                yakuList.push(`寶牌 ${result.doraNum} 飜`);
            }
            if (result.akaDoraNum !== 0) {
                han = han + result.akaDoraNum;
                yakuList.push(`赤寶牌 ${result.akaDoraNum} 飜`);
            }
            if (result.riichiDoraNum !== 0) {
                han = han + result.riichiDoraNum;
                yakuList.push(`裡寶牌 ${result.riichiDoraNum} 飜`);
            }
        }

        result.hanResult = { isYaku: isYaku, yakuman: yakuman, yakumanList: yakumanList, han: han, yakuList: yakuList };
    });

    // 計算點數，採用高點法，只取最高點數的和牌組合，如果和牌點數相同，則取飜數最高的，如果飜數相同，則取符數最高的，如果都相同則取第一組作為結果

    let pointsResults = [];
    results.forEach((result) => {
        // 先考慮役滿
        if (result.hanResult.yakuman !== 0) {
            if (result.isDealer) {
                if (result.isTsumo) {
                    pointsResults.push({ tsumo: 16000 * result.hanResult.yakuman, ron: null }); // 莊家自摸役滿
                } else {
                    pointsResults.push({ tsumo: null, ron: 48000 * result.hanResult.yakuman }); // 莊家榮和役滿
                }
            } else {
                if (result.isTsumo) {
                    pointsResults.push({ tsumo: { dealer: 16000 * result.hanResult.yakuman, nonDealer: 8000 * result.hanResult.yakuman }, ron: null }); // 子家自摸役滿
                } else {
                    pointsResults.push({ tsumo: null, ron: 32000 * result.hanResult.yakuman }); // 子家榮和役滿
                }
            }
        }

        // 考慮一般算飜符情況
        else {
            pointsResults.push(calculatePoints(result.isDealer, result.isTsumo, result.hanResult.han, result.fuResult.fu));
        }
    });

    let maxIndex;
    if (results[0].isDealer && results[0].isTsumo) {
        maxIndex = pointsResults.reduce((maxIndex, current, index, arr) => {
            const maxTsumo = arr[maxIndex].tsumo;
            if (current.tsumo > maxTsumo) {
                return index;
            } else if (current.tsumo === maxTsumo) {
                // 如果 tsumo 相同，則比 han
                const hanA = results[index].hanResult.han;
                const hanB = results[maxIndex].hanResult.han;
                if (hanA > hanB) {
                    return index;
                } else if (hanA === hanB) {
                    // 如果 han 也相同，則比 fu
                    return results[index].fuResult.fuNotCeil > results[maxIndex].fuResult.fuNotCeil ? index : maxIndex;
                }
            }
            return maxIndex;
        }, 0);
    } else if (!results[0].isDealer && results[0].isTsumo) {
        maxIndex = pointsResults.reduce((maxIndex, current, index, arr) => {
            const maxDealerTsumo = arr[maxIndex].tsumo.dealer;
            if (current.tsumo.dealer > maxDealerTsumo) {
                return index;
            } else if (current.tsumo.dealer === maxDealerTsumo) {
                const hanA = results[index].hanResult.han;
                const hanB = results[maxIndex].hanResult.han;
                if (hanA > hanB) {
                    return index;
                } else if (hanA === hanB) {
                    return results[index].fuResult.fuNotCeil > results[maxIndex].fuResult.fuNotCeil ? index : maxIndex;
                }
            }
            return maxIndex;
        }, 0);
    } else {
        maxIndex = pointsResults.reduce((maxIndex, current, index, arr) => {
            const maxRon = arr[maxIndex].ron;
            if (current.ron > maxRon) {
                return index;
            } else if (current.ron === maxRon) {
                const hanA = results[index].hanResult.han;
                const hanB = results[maxIndex].hanResult.han;
                if (hanA > hanB) {
                    return index;
                } else if (hanA === hanB) {
                    return results[index].fuResult.fuNotCeil > results[maxIndex].fuResult.fuNotCeil ? index : maxIndex;
                }
            }
            return maxIndex;
        }, 0);
    }

    const maxPointsResult = {
        ...results[maxIndex],
        pointsResult: pointsResults[maxIndex],
    };

    return maxPointsResult;
};

const calculateFu = (hand) => {
    const meldFuMessage = [];

    let fu = 20; // 基礎符
    let isPairAddFu = false;
    let isPinfuTsumo = false;
    let isChowTwentyFu = false;

    // 面子計算
    hand.melds.forEach((m, i) => {
        const terminalsAndHonours = ["1m", "9m", "1p", "9p", "1s", "9s", "1z", "2z", "3z", "4z", "5z", "6z", "7z"];
        const windMap = {
            東: "1z",
            南: "2z",
            西: "3z",
            北: "4z",
        };

        if (m.type === "手牌順子") {
            meldFuMessage.push("順子 0 符");
        }

        // 判斷手牌刻子是明刻或暗刻
        if (m.type === "手牌刻子") {
            const isTerminalOrHonour = m.tiles.some((tile) => terminalsAndHonours.includes(tile));

            if (hand.isTsumo) {
                fu += isTerminalOrHonour ? 8 : 4; // 如果是自摸必定為暗刻
                meldFuMessage.push(isTerminalOrHonour ? "么九暗刻 8 符" : "中張暗刻 4 符");
            } else {
                if (hand.winningMeldIndex === i) {
                    fu += isTerminalOrHonour ? 4 : 2; // 含榮和牌的刻子為明刻
                    meldFuMessage.push(isTerminalOrHonour ? "么九明刻 4 符" : "中張明刻 2 符");
                } else {
                    fu += isTerminalOrHonour ? 8 : 4; // 不含榮和牌的刻子為為暗刻
                    meldFuMessage.push(isTerminalOrHonour ? "么九暗刻 8 符" : "中張暗刻 4 符");
                }
            }
        }

        if (m.type === "手牌雀頭") {
            if (windMap[hand.roundWind] === m.tiles[0] && windMap[hand.seatWind] !== m.tiles[0]) {
                fu += 2;
                meldFuMessage.push("場風雀頭 2 符");
                isPairAddFu = true;
            } else if (windMap[hand.roundWind] !== m.tiles[0] && windMap[hand.seatWind] === m.tiles[0]) {
                fu += 2;
                meldFuMessage.push("自風雀頭 2 符");
                isPairAddFu = true;
            } else if (windMap[hand.roundWind] === m.tiles[0] && windMap[hand.seatWind] === m.tiles[0]) {
                fu += 4;
                meldFuMessage.push("連風雀頭 4 符");
                isPairAddFu = true;
            } else if (m.tiles.some((tile) => ["5z", "6z", "7z"].includes(tile))) {
                fu += 2;
                meldFuMessage.push("三元牌雀頭 2 符");
                isPairAddFu = true;
            } else {
                meldFuMessage.push("非役牌雀頭 0 符");
                isPairAddFu = false;
            }
        }

        if (m.type === "吃") {
            meldFuMessage.push("順子 0 符");
        }

        if (m.type === "碰") {
            const isTerminalOrHonour = m.tiles.some((tile) => terminalsAndHonours.includes(tile));
            fu += isTerminalOrHonour ? 4 : 2;
            meldFuMessage.push(isTerminalOrHonour ? "么九明刻 4 符" : "中張明刻 2 符");
        }

        if (m.type === "明槓") {
            const isTerminalOrHonour = m.tiles.some((tile) => terminalsAndHonours.includes(tile));
            fu += isTerminalOrHonour ? 16 : 8;
            meldFuMessage.push(isTerminalOrHonour ? "么九明槓 16 符" : "中張明槓 8 符");
        }

        if (m.type === "暗槓") {
            const isTerminalOrHonour = m.tiles.some((tile) => terminalsAndHonours.includes(tile));
            fu += isTerminalOrHonour ? 32 : 16;
            meldFuMessage.push(isTerminalOrHonour ? "么九暗槓 32 符" : "中張暗槓 16 符");
        }
    });

    // 門清榮和加符
    if (hand.isMenzen && !hand.isTsumo) fu += 10;

    // 自摸加符
    if (hand.isTsumo) fu += 2;

    // 單騎邊張嵌張聽牌加符
    if (hand.waitType === "單騎" || hand.waitType === "邊張" || hand.waitType === "嵌張") fu += 2;

    // 平和自摸20符
    if (hand.isMenzen && hand.isTsumo && hand.melds.every((item) => ["手牌順子", "手牌雀頭"].includes(item.type)) && hand.waitType === "兩面" && isPairAddFu === false) {
        isPinfuTsumo = true;
        fu = 20;
    }

    if (
        !hand.isMenzen &&
        !hand.isTsumo &&
        hand.melds.every((item) => ["手牌順子", "手牌雀頭", "吃"].includes(item.type)) &&
        hand.melds.some((item) => ["吃"].includes(item.type)) &&
        hand.waitType === "兩面" &&
        isPairAddFu === false
    ) {
        isChowTwentyFu = true;
        fu = 30;
    }

    return { fuNotCeil: fu, fu: Math.ceil(fu / 10) * 10, meldFuMessage: meldFuMessage, isPinfuTsumo: isPinfuTsumo, isChowTwentyFu: isChowTwentyFu };
};

const calculatePoints = (isDealer, isTsumo, han, fu) => {
    let basePoints;

    // 根據飜數計算基本點數
    if (han >= 13) {
        basePoints = 8000; // 役滿（13飜以上）
    } else if (han >= 11) {
        basePoints = 6000; // 三倍滿（11-12飜）
    } else if (han >= 8) {
        basePoints = 4000; // 倍滿（8-10飜）
    } else if (han >= 6) {
        basePoints = 3000; // 跳滿（6-7飜）
    } else if (han >= 5) {
        basePoints = 2000; // 滿貫（5飜）
    } else {
        // 當飜數低於5飜時，根據符數計算基本點數
        let tempBasePoints = fu * Math.pow(2, han + 2);
        if (tempBasePoints >= 2000) {
            basePoints = 2000;
        } else {
            basePoints = tempBasePoints;
        }
    }

    // 正常處理自摸或榮和情況
    if (isDealer) {
        if (isTsumo) {
            const points = Math.ceil((basePoints * 2) / 100) * 100;
            return { tsumo: points, ron: null };
        } else {
            // 莊家榮和：六倍分數
            const points = Math.ceil((basePoints * 6) / 100) * 100;
            return { tsumo: null, ron: points };
        }
    } else {
        if (isTsumo) {
            // 子家自摸：莊家支付兩倍分數，其他子家支付一次
            const dealerPoints = Math.ceil((basePoints * 2) / 100) * 100;
            const nonDealerPoints = Math.ceil(basePoints / 100) * 100;
            return { tsumo: { dealer: dealerPoints, nonDealer: nonDealerPoints }, ron: null };
        } else {
            // 子家榮和：四倍分數
            const points = Math.ceil((basePoints * 4) / 100) * 100;
            return { tsumo: null, ron: points };
        }
    }
};

const renderHandSequence = (tiles, isWinningMeld, winningTileIndex) => {
    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {tiles.map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: "clamp(25px, 5vw, 50px)",
                        maxWidth: "50px",
                        height: "auto",
                        borderRadius: "5px",
                        filter: isWinningMeld && j === winningTileIndex ? "sepia(130%) hue-rotate(10deg) saturate(500%)" : "",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderHandTriplet = (tiles, isWinningMeld) => {
    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {tiles.map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: "clamp(25px, 5vw, 50px)",
                        maxWidth: "50px",
                        height: "auto",
                        borderRadius: "5px",
                        filter: isWinningMeld && j === 2 ? "sepia(130%) hue-rotate(10deg) saturate(500%)" : "",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderHandPairs = (tiles, isWinningMeld) => {
    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {tiles.map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: "clamp(25px, 5vw, 50px)",
                        maxWidth: "50px",
                        height: "auto",
                        borderRadius: "5px",
                        filter: isWinningMeld && j === 1 ? "sepia(130%) hue-rotate(10deg) saturate(500%)" : "",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderChow = (tiles, randomNumber) => {
    const result = tiles.map((tile, index) => [
        `-${tile}`,
        ...tiles.filter((_, i) => i !== index), // 排除當前 tile，保留其他元素
    ]);

    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {result[Math.floor(randomNumber * result.length)].map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: tile.startsWith("-") ? "calc(1.3 * clamp(25px, 5vw, 50px))" : "clamp(25px, 5vw, 50px)",
                        maxWidth: tile.startsWith("-") ? "65px" : "50px",
                        height: "auto",
                        borderRadius: "5px",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderPong = (tiles, randomNumber) => {
    const result = tiles.map((tile, index) => {
        const newTiles = [...tiles]; // 複製一份原始陣列
        newTiles[index] = `-${tile}`; // 在當前元素上加上 "-"
        return newTiles;
    });

    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {result[Math.floor(randomNumber * result.length)].map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: tile.startsWith("-") ? "calc(1.3 * clamp(25px, 5vw, 50px))" : "clamp(25px, 5vw, 50px)",
                        maxWidth: tile.startsWith("-") ? "65px" : "50px",
                        height: "auto",
                        borderRadius: "5px",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderExposedKong = (tiles, randomNumber) => {
    let result = [];

    // 當加上 `-` 時，分別加在第 1、2 和 4 個位置，且元素為 4
    result.push([`-${tiles[0]}`, tiles[1], tiles[2], tiles[3]]);
    result.push([tiles[0], `-${tiles[1]}`, tiles[2], tiles[3]]);
    result.push([tiles[0], tiles[1], tiles[2], `-${tiles[3]}`]);

    // 當加上 `=` 時，分別加在第 1、2 和 3 個位置，且元素為 3
    if (tiles[0] === "0m" || tiles[0] === "0p" || tiles[0] === "0s") {
        result.push([`v${tiles[0]}`, tiles[1], tiles[2]]);
    } else {
        result.push([`=${tiles[0]}`, tiles[1], tiles[2]]);
    }
    if (tiles[1] === "0m" || tiles[1] === "0p" || tiles[1] === "0s") {
        result.push([tiles[0], `v${tiles[1]}`, tiles[2]]);
    } else {
        result.push([tiles[0], `=${tiles[1]}`, tiles[2]]);
    }
    if (tiles[2] === "0m" || tiles[2] === "0p" || tiles[2] === "0s") {
        result.push([tiles[0], tiles[1], `v${tiles[2]}`]);
    } else {
        result.push([tiles[0], tiles[1], `=${tiles[2]}`]);
    }

    return (
        <div style={{ whiteSpace: "nowrap" }}>
            {result[Math.floor(randomNumber * result.length)].map((tile, j) => (
                <img
                    key={j}
                    src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                    style={{
                        width: tile.startsWith("-") || tile.startsWith("=") || tile.startsWith("v") ? "calc(1.3 * clamp(25px, 5vw, 50px))" : "clamp(25px, 5vw, 50px)",
                        maxWidth: tile.startsWith("-") || tile.startsWith("=") || tile.startsWith("v") ? "65px" : "50px",
                        height: "auto",
                        borderRadius: "5px",
                        display: "inline-block",
                    }}
                    alt={tile}
                />
            ))}
        </div>
    );
};

const renderConcealedKong = (tiles) => {
    if (tiles[0] === "5m" || tiles[0] === "0m") {
        return (
            <div style={{ whiteSpace: "nowrap" }}>
                {tiles.map((tile, j) => (
                    <img
                        key={j}
                        src={j === 0 || j === 3 ? `${process.env.PUBLIC_URL}/images/0z.png` : j === 1 ? `${process.env.PUBLIC_URL}/images/0m.png` : `${process.env.PUBLIC_URL}/images/5m.png`}
                        style={{
                            width: "clamp(25px, 5vw, 50px)",
                            maxWidth: "50px",
                            height: "auto",
                            borderRadius: "5px",
                            display: "inline-block",
                        }}
                        alt={tile}
                    />
                ))}
            </div>
        );
    } else if (tiles[0] === "5s" || tiles[0] === "0s") {
        return (
            <div style={{ whiteSpace: "nowrap" }}>
                {tiles.map((tile, j) => (
                    <img
                        key={j}
                        src={j === 0 || j === 3 ? `${process.env.PUBLIC_URL}/images/0z.png` : j === 1 ? `${process.env.PUBLIC_URL}/images/0s.png` : `${process.env.PUBLIC_URL}/images/5s.png`}
                        style={{
                            width: "clamp(25px, 5vw, 50px)",
                            maxWidth: "50px",
                            height: "auto",
                            borderRadius: "5px",
                            display: "inline-block",
                        }}
                        alt={tile}
                    />
                ))}
            </div>
        );
    } else if (tiles[0] === "5p" || tiles[0] === "0p") {
        return (
            <div style={{ whiteSpace: "nowrap" }}>
                {tiles.map((tile, j) => (
                    <img
                        key={j}
                        src={j === 0 || j === 3 ? `${process.env.PUBLIC_URL}/images/0z.png` : j === 1 ? `${process.env.PUBLIC_URL}/images/0p.png` : `${process.env.PUBLIC_URL}/images/5p.png`}
                        style={{
                            width: "clamp(25px, 5vw, 50px)",
                            maxWidth: "50px",
                            height: "auto",
                            borderRadius: "5px",
                            display: "inline-block",
                        }}
                        alt={tile}
                    />
                ))}
            </div>
        );
    } else {
        return (
            <div style={{ whiteSpace: "nowrap" }}>
                {tiles.map((tile, j) => (
                    <img
                        key={j}
                        src={j === 0 || j === 3 ? `${process.env.PUBLIC_URL}/images/0z.png` : `${process.env.PUBLIC_URL}/images/${tile}.png`}
                        style={{
                            width: "clamp(25px, 5vw, 50px)",
                            maxWidth: "50px",
                            height: "auto",
                            borderRadius: "5px",
                            display: "inline-block",
                        }}
                        alt={tile}
                    />
                ))}
            </div>
        );
    }
};

const HandPointsCalculateTrainer = () => {
    const [handData, setHandData] = useState([]);
    const [userInput, setUserInput] = useState({ dealer: "", nonDealer: "" });
    const [isCorrect, setIsCorrect] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [randomNumber, setRandomNumber] = useState(0);
    const [isTable, setIsTable] = useState(false);

    useEffect(() => {
        initialize();
    }, []);

    const initialize = () => {
        let validData = false;
        let data = [];

        while (!validData) {
            data = generateValidHand();
            validData = data.hanResult.isYaku;
        }

        setHandData(data);
        setShowResult(false);
        setRandomNumber(Math.random());
    };

    const handleInputChange = (e, field) => {
        setUserInput({ ...userInput, [field]: e.target.value });
    };

    const checkAnswer = () => {
        const correctPoints = handData.pointsResult;
        const { dealer, nonDealer } = userInput;

        let isCorrect = false;

        // 根據問題類型檢查答案
        if (handData.isDealer) {
            if (handData.isTsumo) {
                isCorrect = parseInt(dealer) === correctPoints.tsumo;
            } else {
                isCorrect = parseInt(nonDealer) === correctPoints.ron;
            }
        } else {
            if (handData.isTsumo) {
                isCorrect = parseInt(dealer) === correctPoints.tsumo.dealer && parseInt(nonDealer) === correctPoints.tsumo.nonDealer;
            } else {
                isCorrect = parseInt(nonDealer) === correctPoints.ron;
            }
        }

        setIsCorrect(isCorrect);
        setShowResult(true);
    };

    const resetQuestion = () => {
        initialize();
        setUserInput({ dealer: "", nonDealer: "" });
        setIsCorrect(null);
        setShowResult(false);
    };

    const getBackgroundColor = (points) => {
        switch (points) {
            case 4000:
                return "#BBFFEE";
            case 6000:
                return "#CCFF99";
            case 8000:
                return "#FFFFBB";
            case 12000:
                return "#FFDDAA";
            case 16000:
                return "#FFCCCC";
            default:
                return "#EEEEEE";
        }
    };

    // 生成莊家和子家的點數表格
    const generatePointsTable = () => {
        const dealerTableRows = [];
        const nonDealerTableRows = [];
        const hanList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]; // 飜數1-13
        const fuList = [20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 110]; // 符數20-110

        for (let han of hanList) {
            const dealerRow = [];
            const nonDealerRow = [];

            dealerRow.push(
                <td key={`dealer-han-${han}`} style={{ padding: "5px" }}>
                    <div style={{ fontSize: "16px", fontWeight: "bold", display: "inline" }}>{han}飜</div>
                    {han === 13 && <div style={{ fontSize: "16px", fontWeight: "bold", display: "inline" }}>+</div>}
                </td>
            );
            nonDealerRow.push(
                <td key={`non-dealer-han-${han}`} style={{ padding: "5px" }}>
                    <div style={{ fontSize: "16px", fontWeight: "bold", display: "inline" }}>{han}飜</div>
                    {han === 13 && <div style={{ fontSize: "16px", fontWeight: "bold", display: "inline" }}>+</div>}
                </td>
            );

            for (let fu of fuList) {
                const dealerPointsTsumo = calculatePoints(true, true, han, fu);
                const nonDealerPointsTsumo = calculatePoints(false, true, han, fu);
                const dealerPointsRon = calculatePoints(true, false, han, fu);
                const nonDealerPointsRon = calculatePoints(false, false, han, fu);

                if ((han === 1 && fu === 20) || (han === 1 && fu === 25)) {
                    dealerRow.push(
                        <td key={`dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>-</div>
                        </td>
                    );
                    nonDealerRow.push(
                        <td key={`non-dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>-</div>
                        </td>
                    );
                } else if (han === 1 && fu === 110) {
                    dealerRow.push(
                        <td key={`dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{dealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>(-)</div>
                        </td>
                    );

                    nonDealerRow.push(
                        <td key={`non-dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{nonDealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>(-)</div>
                        </td>
                    );
                } else if (han >= 2 && fu === 20) {
                    dealerRow.push(
                        <td key={`dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>-</div>
                            <div style={{ fontSize: "14px" }}>({dealerPointsTsumo.tsumo} ∀)</div>
                        </td>
                    );

                    nonDealerRow.push(
                        <td key={`non-dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>-</div>
                            <div style={{ fontSize: "14px" }}>
                                ({nonDealerPointsTsumo.tsumo.nonDealer} / {nonDealerPointsTsumo.tsumo.dealer})
                            </div>
                        </td>
                    );
                } else if (han === 2 && fu === 25) {
                    dealerRow.push(
                        <td key={`dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{dealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>(-)</div>
                        </td>
                    );

                    nonDealerRow.push(
                        <td key={`non-dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{nonDealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>(-)</div>
                        </td>
                    );
                } else {
                    dealerRow.push(
                        <td key={`dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{dealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>({dealerPointsTsumo.tsumo} ∀)</div>
                        </td>
                    );

                    nonDealerRow.push(
                        <td key={`non-dealer-${han}-${fu}`} style={{ padding: "5px", backgroundColor: getBackgroundColor(dealerPointsTsumo.tsumo) }}>
                            <div style={{ fontSize: "14px", fontWeight: "bold" }}>{nonDealerPointsRon.ron}</div>
                            <div style={{ fontSize: "14px" }}>
                                ({nonDealerPointsTsumo.tsumo.nonDealer} / {nonDealerPointsTsumo.tsumo.dealer})
                            </div>
                        </td>
                    );
                }
            }

            dealerTableRows.push(<tr key={`dealer-${han}`}>{dealerRow}</tr>);
            nonDealerTableRows.push(<tr key={`non-dealer-${han}`}>{nonDealerRow}</tr>);
        }

        return { dealerTableRows, nonDealerTableRows };
    };

    const { dealerTableRows, nonDealerTableRows } = generatePointsTable();

    return (
        <div style={{ maxWidth: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontSize: "clamp(28px, 2vw, 32px)", fontWeight: "bold" }}>手牌算點練習</div>

            {/* 問題展示 */}
            <div style={{ marginTop: "20px", marginBottom: "10px", textAlign: "center" }}>
                <div style={{ fontSize: "clamp(20px, 2vw, 24px)" }}>
                    <div style={{ fontSize: "clamp(20px, 2vw, 24px)", fontWeight: "bold" }}>
                        {handData.roundWind}風場{handData.seatWind}家 {handData.isTsumo ? "自摸" : "榮和"}
                    </div>
                    {(handData.isRiichi || handData.isDaburuRiichi || handData.isIppatsu || handData.isHaitei || handData.isHoutei || handData.isRinshan || handData.isChankan) && (
                        <div style={{ fontSize: "clamp(18px, 2vw, 22px)", display: "inline-flex" }}>
                            {handData.isRiichi && <span style={{ marginRight: "10px" }}>立直</span>}
                            {handData.isDaburuRiichi && <span style={{ marginRight: "10px" }}>雙立直</span>}
                            {handData.isIppatsu && <span style={{ marginRight: "10px" }}>一發</span>}
                            {handData.isHaitei && <span style={{ marginRight: "10px" }}>海底撈月</span>}
                            {handData.isHoutei && <span style={{ marginRight: "10px" }}>河底撈魚</span>}
                            {handData.isRinshan && <span style={{ marginRight: "10px" }}>嶺上開花</span>}
                            {handData.isChankan && <span>搶槓</span>}
                        </div>
                    )}
                </div>
            </div>

            {/* 題目展示 */}
            <div style={{ display: "flex", gap: "30px" }}>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginBottom: "5px" }}>寶牌指示牌</div>
                    {(() => {
                        const doraIndicators = [...(handData?.doraIndicators || []), ...Array(5 - (handData?.doraIndicators || []).length).fill("0z")].slice(0, 5);

                        return doraIndicators.map((tile, index) => (
                            <img
                                key={`${tile}-${index}`}
                                src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                                alt={tile}
                                style={{
                                    width: "clamp(25px, 5vw, 50px)",
                                    maxWidth: "50px",
                                    height: "auto",
                                    borderRadius: "5px",
                                }}
                            />
                        ));
                    })()}
                </div>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginBottom: "5px" }}>裡寶牌指示牌</div>

                    {(() => {
                        const riichiDoraIndicators = [...(handData?.riichiDoraIndicators || []), ...Array(5 - (handData?.riichiDoraIndicators || []).length).fill("0z")].slice(0, 5);

                        return riichiDoraIndicators.map((tile, index) => (
                            <img
                                key={`${tile}-${index}`}
                                src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                                alt={tile}
                                style={{
                                    width: "clamp(25px, 5vw, 50px)",
                                    maxWidth: "50px",
                                    height: "auto",
                                    borderRadius: "5px",
                                }}
                            />
                        ));
                    })()}
                </div>
            </div>

            <div
                className="hand-tiles"
                style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    marginTop: "10px",
                    width: "100%",
                    overflowX: "auto",
                    justifyContent: "flex-start", // 讓表格從左側開始，不影響捲動
                }}
            >
                <div style={{ margin: "auto" }}>
                    <table style={{ whiteSpace: "nowrap" }}>
                        <tbody>
                            <tr>
                                <td style={{ verticalAlign: "bottom", padding: "2px" }}>
                                    {(() => {
                                        const filteredTiles = (handData?.melds || [])
                                            .map((meld, meldIndex) =>
                                                meldIndex === handData.winningMeldIndex ? { ...meld, tiles: meld.tiles.filter((_, tileIndex) => tileIndex !== handData.winningTileIndex) } : meld
                                            )
                                            .filter((meld) => ["手牌刻子", "手牌順子", "手牌雀頭"].includes(meld.type))
                                            .flatMap((meld) => meld.tiles);

                                        return sortTiles(filteredTiles).map((tile, index) => (
                                            <img
                                                key={`${tile}-${index}`}
                                                src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                                                alt={tile}
                                                style={{
                                                    width: "clamp(25px, 5vw, 50px)",
                                                    maxWidth: "50px",
                                                    height: "auto",
                                                    borderRadius: "5px",
                                                }}
                                            />
                                        ));
                                    })()}
                                </td>
                                <td style={{ verticalAlign: "bottom", padding: "2px" }}>
                                    {(() => {
                                        const winningMeld = (handData?.melds || [])[handData.winningMeldIndex];
                                        const filteredTiles = winningMeld ? [winningMeld.tiles[handData.winningTileIndex]] : [];

                                        return filteredTiles.map((tile, index) => (
                                            <img
                                                key={`${tile}-${index}`}
                                                src={`${process.env.PUBLIC_URL}/images/${tile}.png`}
                                                alt={tile}
                                                style={{
                                                    width: "clamp(25px, 5vw, 50px)",
                                                    maxWidth: "50px",
                                                    height: "auto",
                                                    borderRadius: "5px",
                                                    filter: "sepia(130%) hue-rotate(10deg) saturate(500%)",
                                                }}
                                            />
                                        ));
                                    })()}
                                </td>
                                {(() => {
                                    const filteredTiles = (handData?.melds || []).filter((meld) => ["吃", "碰", "明槓", "暗槓"].includes(meld.type));

                                    return filteredTiles.map((meld, i) => (
                                        <td key={i} style={{ verticalAlign: "bottom", padding: "2px" }}>
                                            {meld.type === "吃" && renderChow(meld.tiles, randomNumber)}
                                            {meld.type === "碰" && renderPong(meld.tiles, randomNumber)}
                                            {meld.type === "明槓" && renderExposedKong(meld.tiles, randomNumber)}
                                            {meld.type === "暗槓" && renderConcealedKong(meld.tiles)}
                                        </td>
                                    ));
                                })()}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 輸入區域 */}
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                {handData.isDealer ? (
                    handData.isTsumo ? (
                        <div>
                            <label style={{ fontSize: "20px" }}>
                                <input
                                    type="number"
                                    value={userInput.dealer}
                                    onChange={(e) => handleInputChange(e, "dealer")}
                                    style={{
                                        width: "130px",
                                        padding: "5px",
                                        border: "2px solid #000000",
                                        borderRadius: "5px",
                                        fontSize: "20px",
                                        outline: "none",
                                        marginRight: "10px",
                                    }}
                                />
                                點 ∀
                            </label>
                        </div>
                    ) : (
                        <div>
                            <label style={{ fontSize: "20px" }}>
                                <input
                                    type="number"
                                    value={userInput.nonDealer}
                                    onChange={(e) => handleInputChange(e, "nonDealer")}
                                    style={{
                                        width: "130px",
                                        padding: "5px",
                                        border: "2px solid #000000",
                                        borderRadius: "5px",
                                        fontSize: "20px",
                                        outline: "none",
                                        marginRight: "10px",
                                    }}
                                />
                                點
                            </label>
                        </div>
                    )
                ) : handData.isTsumo ? (
                    <div>
                        <label style={{ fontSize: "20px" }}>
                            <input
                                type="number"
                                value={userInput.nonDealer}
                                onChange={(e) => handleInputChange(e, "nonDealer")}
                                placeholder="其他子家"
                                style={{
                                    width: "130px",
                                    padding: "5px",
                                    border: "2px solid #000000",
                                    borderRadius: "5px",
                                    fontSize: "20px",
                                    outline: "none",
                                    marginRight: "10px",
                                }}
                            />
                            點 /
                            <input
                                type="number"
                                value={userInput.dealer}
                                onChange={(e) => handleInputChange(e, "dealer")}
                                placeholder="莊家"
                                style={{
                                    width: "130px",
                                    padding: "5px",
                                    border: "2px solid #000000",
                                    borderRadius: "5px",
                                    fontSize: "20px",
                                    outline: "none",
                                    marginLeft: "10px",
                                    marginRight: "10px",
                                }}
                            />
                            點
                        </label>
                    </div>
                ) : (
                    <div>
                        <label style={{ fontSize: "20px" }}>
                            <input
                                type="number"
                                value={userInput.nonDealer}
                                onChange={(e) => handleInputChange(e, "nonDealer")}
                                style={{
                                    width: "130px",
                                    padding: "5px",
                                    border: "2px solid #000000",
                                    borderRadius: "5px",
                                    fontSize: "20px",
                                    outline: "none",
                                    marginRight: "10px",
                                }}
                            />
                            點
                        </label>
                    </div>
                )}
            </div>

            {/* 按鈕 */}
            <div style={{ justifyContent: "center", display: "flex", gap: "15px" }}>
                <button
                    onClick={checkAnswer}
                    style={{
                        padding: "7px 15px",
                        fontSize: "20px",
                        fontWeight: "bold",
                        backgroundColor: "#007BFF",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                    }}
                >
                    提交答案
                </button>
                <button
                    onClick={resetQuestion}
                    style={{
                        padding: "7px 15px",
                        fontSize: "20px",
                        fontWeight: "bold",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                    }}
                >
                    下一題
                </button>
            </div>

            <button
                onClick={() => setIsTable(true)}
                style={{
                    margin: "10px 0",
                    padding: "7px 15px",
                    fontSize: "20px",
                    fontWeight: "bold",
                    backgroundColor: "#FF9800",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                }}
            >
                役種點數表
            </button>

            {/* 結果顯示 */}
            {showResult && (
                <>
                    {
                        <div
                            style={{
                                margin: "20px 0",
                                padding: "10px 30px",
                                backgroundColor: isCorrect ? "#d4edda" : "#f8d7da",
                                border: `2px solid ${isCorrect ? "#28a745" : "#dc3545"}`,
                                borderRadius: "10px",
                                justifySelf: "center",
                                fontSize: "20px",
                                fontWeight: "bold",

                                textAlign: "center",
                            }}
                        >
                            <div style={{ color: isCorrect ? "#28a745" : "#dc3545" }}>{isCorrect ? "正確！" : `錯誤！`}</div>
                            <div style={{ marginTop: "10px" }}>正確答案：</div>
                            <div>
                                {handData?.isDealer ? "莊家" : "子家"}
                                {handData?.isTsumo ? " 自摸" : " 榮和"}

                                {handData?.hanResult?.yakuman !== 0 ? ` ${handData.hanResult.yakuman} 倍役滿` : handData?.hanResult?.han !== undefined ? ` ${handData.hanResult?.han} 飜` : null}
                                {handData?.hanResult?.yakuman === 0 && handData?.fuResult?.fu !== undefined && ` ${handData.fuResult.fu} 符`}
                            </div>

                            {handData.isDealer && handData.isTsumo && <div style={{ fontWeight: "bold", fontSize: "20px" }}>{handData.pointsResult.tsumo} 點 ∀</div>}
                            {handData.isDealer && !handData.isTsumo && <div style={{ fontWeight: "bold", fontSize: "20px" }}>{handData.pointsResult.ron} 點</div>}
                            {!handData.isDealer && handData.isTsumo && (
                                <div style={{ fontWeight: "bold", fontSize: "20px" }}>
                                    {handData.pointsResult.tsumo.nonDealer} 點 / {handData.pointsResult.tsumo.dealer} 點
                                </div>
                            )}
                            {!handData.isDealer && !handData.isTsumo && <div style={{ fontWeight: "bold", fontSize: "20px" }}>{handData.pointsResult.ron} 點</div>}
                        </div>
                    }

                    <div style={{ fontSize: "clamp(18px, 2vw, 22px)", fontWeight: "bold", marginTop: "30px" }}>飜數計算</div>
                    {handData.hanResult.yakumanList && handData.hanResult.yakumanList.length > 0 && (
                        <>
                            <div
                                style={{
                                    fontSize: "calc(0.8*clamp(18px, 2vw, 22px))",
                                    display: "flex", // 改成 flex
                                    flexWrap: "wrap", // 允許超出時換行
                                    gap: "10px 20px",
                                    marginTop: "10px",
                                    justifyContent: "center",
                                }}
                            >
                                {handData.hanResult.yakumanList.map((yaku, index) => (
                                    <div key={index} style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                        {yaku}
                                    </div>
                                ))}
                            </div>

                            <div style={{ fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginTop: "10px" }}>{handData.hanResult.yakuman} 倍役滿</div>
                        </>
                    )}
                    {handData.hanResult.yakuList && handData.hanResult.yakuList.length > 0 && (
                        <>
                            <div
                                style={{
                                    fontSize: "calc(0.8*clamp(18px, 2vw, 22px))",
                                    display: "flex", // 改成 flex
                                    flexWrap: "wrap", // 允許超出時換行
                                    gap: "10px 20px",
                                    marginTop: "10px",
                                    justifyContent: "center",
                                }}
                            >
                                {handData.hanResult.yakuList.map((yaku, index) => (
                                    <div key={index} style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                        {yaku}
                                    </div>
                                ))}
                            </div>

                            <div style={{ fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginTop: "10px" }}>共 {handData.hanResult.han} 飜</div>
                        </>
                    )}

                    {handData.hanResult.yakuman === 0 && (
                        <>
                            <div
                                style={{
                                    maxWidth: "100%",
                                    textAlign: "center",
                                    overflow: "auto",
                                }}
                            >
                                <div style={{ fontSize: "clamp(18px, 2vw, 22px)", fontWeight: "bold", marginTop: "30px" }}>符數計算</div>
                                <table style={{ marginTop: "10px" }}>
                                    <thead>
                                        <tr>
                                            {(handData?.melds || []).map((meld, i) => (
                                                <td key={i} style={{ verticalAlign: "bottom", padding: "2px", justifyItems: "center" }}>
                                                    {meld.type === "手牌順子" && renderHandSequence(meld.tiles, handData.winningMeldIndex === i, handData.winningTileIndex)}
                                                    {meld.type === "手牌刻子" && renderHandTriplet(meld.tiles, handData.winningMeldIndex === i)}
                                                    {meld.type === "手牌雀頭" && renderHandPairs(meld.tiles, handData.winningMeldIndex === i)}
                                                    {meld.type === "吃" && renderChow(meld.tiles, randomNumber)}
                                                    {meld.type === "碰" && renderPong(meld.tiles, randomNumber)}
                                                    {meld.type === "明槓" && renderExposedKong(meld.tiles, randomNumber)}
                                                    {meld.type === "暗槓" && renderConcealedKong(meld.tiles)}
                                                </td>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            {(handData.fuResult?.meldFuMessage || []).map((m, i) => (
                                                <td key={i} style={{ height: "40px", textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>
                                                    {m}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {!handData.fuResult.isPinfuTsumo && !handData.fuResult.isChowTwentyFu && (
                                <div style={{ display: "inline-flex", gap: "20px", marginTop: "10px" }}>
                                    <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>底符 20 符</div>
                                    {!handData.isTsumo && handData.isMenzen ? <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>門清榮和 10 符</div> : null}
                                    {handData.isTsumo ? <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>自摸和 2 符</div> : null}
                                    {handData.waitType === "單騎" ? <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>單騎聽牌 2 符</div> : null}
                                    {handData.waitType === "邊張" ? <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>邊張聽牌 2 符</div> : null}
                                    {handData.waitType === "嵌張" ? <div style={{ textAlign: "center", fontSize: "calc(0.8*clamp(18px, 2vw, 22px))" }}>嵌張聽牌 2 符</div> : null}
                                </div>
                            )}

                            {!handData.fuResult.isPinfuTsumo && !handData.fuResult.isChowTwentyFu && (
                                <div style={{ display: "inline-flex", gap: "20px", marginTop: "10px" }}>
                                    {handData.fuResult.fuNotCeil === handData.fuResult.fu && (
                                        <div style={{ textAlign: "center", fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold" }}>共 {handData.fuResult.fu} 符</div>
                                    )}
                                    {handData.fuResult.fuNotCeil !== handData.fuResult.fu && (
                                        <div style={{ textAlign: "center", fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", display: "inline-flex", gap: "10px" }}>
                                            <div>共 {handData.fuResult.fuNotCeil} 符</div>
                                            <div>進位 {handData.fuResult.fu} 符</div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {handData.fuResult.isPinfuTsumo && (
                                <div style={{ textAlign: "center", fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginTop: "10px" }}>平和自摸 20 符</div>
                            )}
                            {handData.fuResult.isChowTwentyFu && (
                                <div style={{ textAlign: "center", fontSize: "calc(0.9*clamp(18px, 2vw, 22px))", fontWeight: "bold", marginTop: "10px" }}>副露平和型計為 30 符</div>
                            )}
                        </>
                    )}
                </>
            )}

            <Modal open={isTable} onClose={() => setIsTable(false)}>
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "white",
                        padding: "10px",
                        borderRadius: "8px",
                        width: "90%",
                        maxHeight: "80vh",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        style={{
                            position: "relative",
                            textAlign: "center",
                            padding: "10px 0",
                        }}
                    >
                        <IconButton
                            onClick={() => setIsTable(false)}
                            style={{
                                position: "absolute",
                                top: "5px",
                                right: "10px",
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>役種點數表</div>
                    </div>

                    <div
                        style={{
                            overflowX: "auto",
                            flexGrow: 1, // 讓內容填滿剩餘空間
                            display: "flex",
                            flexDirection: "column",
                            textAlign: "center",
                        }}
                    >
                        <table border="1" style={{ width: "100%", marginTop: "10px", borderCollapse: "collapse", textAlign: "center" }}>
                            <tr>
                                <td colSpan={2}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", margin: "10px 0" }}>役種表</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>斷么九 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌不可包含么九牌和字牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["2m", "3m", "4m"], false)}
                                        {renderHandTriplet(["3s", "4s", "5s"], false)}
                                        {renderHandPairs(["8s", "8s"], false)}
                                        {renderPong(["7p", "7p", "7p"], 0.5)}
                                        {renderChow(["6m", "7m", "8m"], 0.5)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>役牌 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>役牌包含場風牌、自風牌和三元牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold", margin: "5px" }}>場風：東</div>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["1z", "1z", "1z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold", margin: "5px" }}>自風：西</div>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["3z", "3z", "3z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold", margin: "5px" }}>三元牌（白發中）</div>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["5z", "5z", "5z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>立直 1 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>
                                        門清聽牌時，可支付 1000 點宣告立直，之後不可改變牌型（根據不同規則有所不同），並且在和牌之前只能摸切（在條件允許的情況下可以進行暗槓）。
                                    </div>
                                    <div style={{ fontSize: "16px" }}>立直後如果有牌不和則觸發立直振聽，之後只能自摸和牌。</div>
                                    <div style={{ fontSize: "16px" }}>如果規則存在裡寶牌，則立直和牌後可以翻開與寶牌指示牌相同數量的裡寶牌指示牌。</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>一發 1 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>立直後一巡內和牌（自摸榮和都可），並且期間不可有人吃、碰、槓、拔北（三麻限定）。</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>門清自摸和 1 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>門清狀態下自摸和牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>平和 1 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌僅由順子和雀頭組成，雀頭不可是役牌，並且兩面聽牌</div>
                                    <div style={{ fontSize: "16px" }}>平和自摸計為 2 飜 20 符</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>一杯口 1 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有 2 組相同的順子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["5m", "6m", "7m"], false)}
                                        {renderHandTriplet(["5m", "6m", "7m"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>河底撈魚 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>榮和最後一張打出的牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>海底撈月 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>自摸最後一張牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>搶槓 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>榮和別家加槓的牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>嶺上開花 1 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>開槓後嶺上牌自摸</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>對對和 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌僅由刻子和槓子組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["3m", "3m", "3m"], false)}
                                        {renderHandPairs(["6p", "6p"], false)}
                                        {renderPong(["9s", "9s", "9s"], 0.5)}
                                        {renderExposedKong(["2z", "2z", "2z", "2z"], 0.3)}
                                        {renderConcealedKong(["6z", "6z", "6z", "6z"])}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>三暗刻 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌的暗刻和暗槓有 3 組</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignContent: "center" }}>
                                        {renderHandTriplet(["3m", "3m", "3m"], false)}
                                        {renderHandTriplet(["6p", "6p", "6p"], false)}
                                        {renderConcealedKong(["9s", "9s", "9s", "9s"])}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>三槓子 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌的槓子有 3 組</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderExposedKong(["3m", "3m", "3m", "3m"], 0.3)}
                                        {renderExposedKong(["6p", "6p", "6p", "6p"], 0.9)}
                                        {renderConcealedKong(["9s", "9s", "9s", "9s"])}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>三色同刻 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有 3 組相同數字但不同花色的刻子或槓子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["3m", "3m", "3m"], false)}
                                        {renderPong(["3p", "3p", "3p"], 0.8)}
                                        {renderExposedKong(["3s", "3s", "3s", "3s"], 0.9)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>混老頭 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌全部由么九牌或字牌組成，並且不可全由字牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "1m", "1m"], false)}
                                        {renderHandPairs(["3z", "3z"], false)}
                                        {renderPong(["9m", "9m", "9m"], 0.8)}
                                        {renderExposedKong(["1s", "1s", "1s", "1s"], 0.9)}
                                        {renderConcealedKong(["1p", "1p", "1p", "1p"])}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>小三元 2 飜</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有 2 組三元牌的刻子或槓子，加上 1 組三元牌的雀頭</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["5z", "5z", "5z"], false)}
                                        {renderHandPairs(["7z", "7z"], false)}
                                        {renderPong(["6z", "6z", "6z"], 0.8)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>三色同順 2 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有 3 組相同數字但不同花色的順子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["2m", "3m", "4m"], false)}
                                        {renderHandTriplet(["2p", "3p", "4p"], false)}
                                        {renderHandTriplet(["2s", "3s", "4s"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>一氣通貫 2 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有同花色的 123、456、789 三組順子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["4m", "5m", "6m"], false)}
                                        {renderHandTriplet(["7m", "8m", "9m"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>混全帶么九 2 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌所有的面子和雀頭都包含么九牌或字牌，並且必須有順子，必須有字牌，不可全由字牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["7m", "8m", "9m"], false)}
                                        {renderHandTriplet(["1p", "1p", "1p"], false)}
                                        {renderHandTriplet(["7s", "8s", "9s"], false)}
                                        {renderHandPairs(["3z", "3z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>七對子 2 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌有 7 組不同的對子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandPairs(["3m", "3m"], false)}
                                        {renderHandPairs(["7m", "7m"], false)}
                                        {renderHandPairs(["2p", "2p"], false)}
                                        {renderHandPairs(["5p", "5p"], false)}
                                        {renderHandPairs(["1s", "1s"], false)}
                                        {renderHandPairs(["8s", "8s"], false)}
                                        {renderHandPairs(["3z", "3z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>雙立直 2 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>在第一巡宣告立直，並且在立直前不可有任何吃、碰、槓和拔北（三麻限定）</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>混一色 3 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌所有的面子和雀頭都由相同花色的數牌和字牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["4m", "4m", "4m"], false)}
                                        {renderHandTriplet(["5m", "6m", "7m"], false)}
                                        {renderHandTriplet(["9m", "9m", "9m"], false)}
                                        {renderHandPairs(["3z", "3z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>純全帶么九 3 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌所有的面子和雀頭都包含么九牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["9m", "9m", "9m"], false)}
                                        {renderHandTriplet(["1p", "1p", "1p"], false)}
                                        {renderHandTriplet(["7p", "8p", "9p"], false)}
                                        {renderHandPairs(["9s", "9s"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>兩杯口 3 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（限門清）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 2 組一杯口（不可算做七對子）</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["1p", "2p", "3p"], false)}
                                        {renderHandTriplet(["1p", "2p", "3p"], false)}
                                        {renderHandPairs(["9s", "9s"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>清一色 6 飜</div>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>（副露減 1 飜）</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌由相同花色的數牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "2m", "3m"], false)}
                                        {renderHandTriplet(["4m", "4m", "4m"], false)}
                                        {renderHandTriplet(["5m", "6m", "7m"], false)}
                                        {renderHandTriplet(["8m", "8m", "8m"], false)}
                                        {renderHandPairs(["9m", "9m"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>天和 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>莊家初始配牌和牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>地和 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>子家首次自摸和牌，且和牌前不可有任何吃、碰、槓和拔北（三麻限定）</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>大三元 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 3 組三元牌的刻子或槓子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["5z", "5z", "5z"], false)}
                                        {renderHandTriplet(["6z", "6z", "6z"], false)}
                                        {renderHandTriplet(["7z", "7z", "7z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>小四喜 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 3 組風牌的刻子或槓子，以及 1 組風牌的雀頭</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1z", "1z", "1z"], false)}
                                        {renderHandTriplet(["2z", "2z", "2z"], false)}
                                        {renderHandTriplet(["3z", "3z", "3z"], false)}
                                        {renderHandPairs(["4z", "4z"], false)}
                                        {renderHandTriplet(["0z", "0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>大四喜 兩倍役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 4 組風牌的刻子或槓子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1z", "1z", "1z"], false)}
                                        {renderHandTriplet(["2z", "2z", "2z"], false)}
                                        {renderHandTriplet(["3z", "3z", "3z"], false)}
                                        {renderHandTriplet(["4z", "4z", "4z"], false)}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>字一色 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌全由字牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1z", "1z", "1z"], false)}
                                        {renderHandTriplet(["2z", "2z", "2z"], false)}
                                        {renderHandTriplet(["3z", "3z", "3z"], false)}
                                        {renderHandTriplet(["6z", "6z", "6z"], false)}
                                        {renderHandPairs(["7z", "7z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>綠一色 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌只包含 23468 索和發</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["2s", "3s", "4s"], false)}
                                        {renderHandTriplet(["2s", "3s", "4s"], false)}
                                        {renderHandTriplet(["6s", "6s", "6s"], false)}
                                        {renderHandTriplet(["8s", "8s", "8s"], false)}
                                        {renderHandPairs(["6z", "6z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>清老頭 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌只包含刻子或槓子，且都由么九牌組成</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "1m", "1m"], false)}
                                        {renderHandTriplet(["9m", "9m", "9m"], false)}
                                        {renderHandTriplet(["1p", "1p", "1p"], false)}
                                        {renderHandTriplet(["9p", "9p", "9p"], false)}
                                        {renderHandPairs(["1s", "1s"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>國士無雙 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含么九牌和字牌 13 張各一張，以及額外 1 張么九牌或字牌，且聽牌時只有 12 種么九牌和字牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "9m", "1s", "9s", "1p", "9p", "1z", "1z", "2z", "3z", "4z", "5z", "6z"], false)}
                                        {renderHandTriplet(["7z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>國士無雙 13 面 兩倍役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含么九牌和字牌 13 張各一張，以及額外 1 張么九牌或字牌，且聽牌時 13 面聽牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "9m", "1s", "9s", "1p", "9p", "1z", "2z", "3z", "4z", "5z", "6z", "7z"], false)}
                                        {renderHandTriplet(["7z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>四暗刻 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 4 組暗刻或暗槓</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["3m", "3m", "3m"], false)}
                                        {renderHandTriplet(["5p", "5p", "5p"], false)}
                                        {renderHandTriplet(["8s", "8s", "8s"], false)}
                                        {renderHandTriplet(["1z", "1z"], false)}
                                        {renderHandPairs(["6z", "6z"], false)}
                                        {renderHandPairs(["6z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>四暗刻單騎 兩倍役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 4 組暗刻或暗槓，且單騎聽牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["3m", "3m", "3m"], false)}
                                        {renderHandTriplet(["5p", "5p", "5p"], false)}
                                        {renderHandTriplet(["8s", "8s", "8s"], false)}
                                        {renderHandTriplet(["1z", "1z", "1z"], false)}
                                        {renderHandPairs(["6z"], false)}
                                        {renderHandPairs(["6z"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>九蓮寶燈 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含相同花色的 1112345678999，再加上相同花色的數牌 1 張，且聽牌時非 9 面聽牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "1m", "1m", "2m", "3m", "4m", "4m", "6m", "7m", "8m", "9m", "9m", "9m"], false)}
                                        {renderHandTriplet(["5m"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>純正九蓮寶燈 兩倍役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含相同花色的 1112345678999，再加上相同花色的數牌 1 張，且聽牌時 9 面聽牌</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderHandTriplet(["1m", "1m", "1m", "2m", "3m", "4m", "5m", "6m", "7m", "8m", "9m", "9m", "9m"], false)}
                                        {renderHandTriplet(["5m"], false)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>四槓子 役滿</div>
                                </td>
                                <td style={{ padding: "10px", justifyItems: "center", alignContent: "center" }}>
                                    <div style={{ fontSize: "16px" }}>手牌包含 4 組槓子</div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ verticalAlign: "bottom", padding: "10px 10px" }}>
                                    <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "flex-end" }}>
                                        {renderExposedKong(["3m", "3m", "3m", "3m"], 0.1)}
                                        {renderExposedKong(["7p", "7p", "7p", "7p"], 0.5)}
                                        {renderExposedKong(["2s", "2s", "2s", "2s"], 0.9)}
                                        {renderConcealedKong(["6z", "6z", "6z", "6z"])}
                                        {renderHandPairs(["0z", "0z"], false)}
                                    </div>
                                </td>
                            </tr>
                        </table>

                        <table border="1" style={{ width: "100%", marginTop: "50px", borderCollapse: "collapse" }}>
                            <tr>
                                <td colSpan={12}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "10px 0", gap: "15px", flexWrap: "wrap" }}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div style={{ width: "20px", height: "20px", background: "#BBFFEE", border: "3px solid #33FFFF", borderRadius: "5px", marginRight: "5px" }}></div>
                                            <span style={{ fontWeight: "bold" }}>滿貫</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div style={{ width: "20px", height: "20px", background: "#CCFF99", border: "3px solid #99FF33", borderRadius: "5px", marginRight: "5px" }}></div>
                                            <span style={{ fontWeight: "bold" }}>跳滿</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div style={{ width: "20px", height: "20px", background: "#FFFFBB", border: "3px solid #FFFF00", borderRadius: "5px", marginRight: "5px" }}></div>
                                            <span style={{ fontWeight: "bold" }}>倍滿</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div style={{ width: "20px", height: "20px", background: "#FFDDAA", border: "3px solid #FFBB66", borderRadius: "5px", marginRight: "5px" }}></div>
                                            <span style={{ fontWeight: "bold" }}>三倍滿</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div style={{ width: "20px", height: "20px", background: "#FFCCCC", border: "3px solid #FF8888", borderRadius: "5px", marginRight: "5px" }}></div>
                                            <span style={{ fontWeight: "bold" }}>役滿</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={12}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", margin: "10px 0" }}>莊家點數表</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "5px" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>飜符數</div>
                                </td>
                                {["20", "25", "30", "40", "50", "60", "70", "80", "90", "100", "110"].map((fu, index) => (
                                    <td key={fu} style={{ padding: "5px" }}>
                                        <div style={{ fontSize: "16px", fontWeight: "bold" }}>{fu}符</div>
                                    </td>
                                ))}
                            </tr>
                            {dealerTableRows}
                            <tr>
                                <td colSpan={12}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", margin: "10px 0" }}>子家點數表</div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: "5px" }}>
                                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>飜符數</div>
                                </td>
                                {["20", "25", "30", "40", "50", "60", "70", "80", "90", "100", "110"].map((fu, index) => (
                                    <td key={fu} style={{ padding: "5px" }}>
                                        <div style={{ fontSize: "16px", fontWeight: "bold" }}>{fu}符</div>
                                    </td>
                                ))}
                            </tr>
                            {nonDealerTableRows}
                        </table>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default HandPointsCalculateTrainer;
